import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { capitalize } from 'lodash'
import { Link, useHistory } from 'react-router-dom'
import { IconTrash } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { PageHeader } from '@/components/headers/PageHeader'
import { Pill } from '@/components/pills/Pill'
import { Card } from '@/components/cards/Card'
import { toast } from '@/components/toasts/toast.helpers'
import {
  getAndCheckOrgColumn,
  getConnectorDisplayType,
  getStatusClassName,
  mergeOrgsandIntegrations,
} from '@/views/Integrations/helpers'
import { IntegrationsForm } from '@/views/Integrations/IntegrationsForm'
import { deleteIntegration, fetchIntegrations } from '@/reducers/integrations/integrations.actions'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { apiService, fetchingAPI } from '@/api'
import { formatTime } from '@/utils/helpers'

import { ModalActions } from '@/components/modals/ModalActions'
import { BasicModal } from '@/components/layout/modals/BasicModal'

// The unique-outcomes refresh is a slow, multi-minute operation (a full seq
// scan + DISTINCT over outcomes.call_outcomes — measured ~2 min and growing
// with the table). Poll its status on a relaxed cadence: the spinner lingering
// up to one interval past completion is negligible next to a 2-minute run, and
// it keeps status requests (which hit the DB primary) infrequent.
const OUTCOMES_REFRESH_POLL_INTERVAL_MS = 20000

const IntegrationsPage = () => {
  const dispatch = useDispatch()
  const { integrations, loading } = useSelector((state) => state.integrations)
  const organizations = useSelector((state) => state.organizations)
  const history = useHistory()

  // The unique-outcomes refresh runs asynchronously on the backend (it can take
  // several minutes). We disable the button and poll a status endpoint while it
  // runs so the user gets clear feedback instead of "I clicked but nothing
  // happened" — and so concurrent clicks (this tab, other tabs, other users)
  // can't kick off duplicate runs.
  const [isRefreshingOutcomes, setIsRefreshingOutcomes] = useState(false)
  const outcomesPollRef = useRef(null)

  const uniqueOutcomesRefreshUrl = `${apiService.core}/api/integration/unique_outcomes_refresh`

  const stopPollingOutcomes = () => {
    if (outcomesPollRef.current) {
      clearInterval(outcomesPollRef.current)
      outcomesPollRef.current = null
    }
  }

  // Returns 'running' | 'idle' | 'unknown'. 'unknown' means the status request
  // itself failed (network blip, auth expiry, backend 5xx) -- deliberately kept
  // distinct from a confirmed 'idle' so the poll loop keeps waiting rather than
  // reporting a false success on a transient error mid-refresh.
  const checkOutcomesRefreshStatus = async () => {
    try {
      const res = await fetchingAPI(`${uniqueOutcomesRefreshUrl}/status`, 'GET', dispatch)
      return res?.running ? 'running' : 'idle'
    } catch (err) {
      console.error(err)
      return 'unknown'
    }
  }

  const pollOutcomesRefreshStatus = () => {
    stopPollingOutcomes()
    outcomesPollRef.current = setInterval(async () => {
      const status = await checkOutcomesRefreshStatus()
      // Only a confirmed 'idle' means the refresh finished. 'running' and
      // 'unknown' both keep the loop going.
      if (status === 'idle') {
        stopPollingOutcomes()
        setIsRefreshingOutcomes(false)
        toast.success(`Successfully refreshed outcomes`)
      }
    }, OUTCOMES_REFRESH_POLL_INTERVAL_MS)
  }

  const refreshUniqueOutcomesMaterializedView = async () => {
    if (isRefreshingOutcomes) {
      return
    }
    setIsRefreshingOutcomes(true)
    try {
      await fetchingAPI(uniqueOutcomesRefreshUrl, 'POST', dispatch)
      toast.success(`Refreshing outcomes, this may take a few minutes...`)
      pollOutcomesRefreshStatus()
    } catch (err) {
      // 409 -> a refresh is already running (another user/tab). Show the same
      // processing state and poll to completion instead of erroring.
      if (err?.status === 'in_progress') {
        toast.info(`An outcomes refresh is already in progress...`)
        pollOutcomesRefreshStatus()
      } else {
        console.error(err)
        toast.error(`Error refreshing outcomes`, { cause: err })
        setIsRefreshingOutcomes(false)
      }
    }
  }

  useEffect(() => {
    dispatch(fetchIntegrations())
  }, [])

  // On load, reflect any refresh already in progress so the button starts in
  // its processing state rather than inviting a duplicate click.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const status = await checkOutcomesRefreshStatus()
      if (!cancelled && status === 'running') {
        setIsRefreshingOutcomes(true)
        pollOutcomesRefreshStatus()
      }
    })()
    return () => {
      cancelled = true
      stopPollingOutcomes()
    }
  }, [])

  const columns = [
    {
      accessor: 'name',
      isSearchable: true,
      label: 'Name',
    },
    {
      accessor: 'type',
      isSearchable: true,
      label: 'Connector Type',
    },
    {
      accessor: 'organization_name',
      label: 'Organization',
      isSearchable: true,
    },
    {
      accessor: 'organization_id',
      label: 'Org Id',
      isSearchable: true,
    },
    {
      accessor: 'status',
      label: 'Status',
    },
    {
      accessor: 'modified_date',
      label: 'Last Updated',
      format: (value) => formatTime(value),
    },
  ]

  const actions = [
    {
      label: 'Delete',
      trigger: (row) => (
        <BasicModal
          trigger={(handleOpen) => (
            <Button basic small square onClick={handleOpen} tooltip="Delete Integration">
              <IconTrash />
            </Button>
          )}
          title="Delete Integration"
          size="tiny"
        >
          {(handleClose) => (
            <ModalActions
              onClose={handleClose}
              onSubmit={() => {
                if (row.active) {
                  toast.error(`Must be deactivated before deletion`)
                } else {
                  dispatch(deleteIntegration({ id: row.id, push: history.push }))
                }
              }}
              isDelete
            >
              <p>{`Are you sure you would like to delete [${row.name.value}]'s [${row.type}] integration config?`}</p>
            </ModalActions>
          )}
        </BasicModal>
      ),
    },
  ]

  const rows = mergeOrgsandIntegrations(integrations, organizations).map((integration) => ({
    ...integration,
    name: {
      as: (
        <Link to={`/integrations/edit_config/${integration.id}`} className="table-link">
          {' '}
          {integration.name}
        </Link>
      ),
      value: integration.name,
    },
    status: {
      as: (
        <Pill small {...getStatusClassName(integration.status)}>
          {capitalize(integration.status.replace('_', ' '))}
        </Pill>
      ),
      value: integration.status,
    },
    organization_id: {
      as: getAndCheckOrgColumn(integration.organization_id),
      value: integration.organization_id,
    },
    type: getConnectorDisplayType(integration.type), // format display value (ex: genesys -> genesys cloud)
  }))
  return (
    <>
      <PageHeader
        title="Integrations"
        data-testid="integrations-page"
        actions={
          <>
            <ButtonAndFormModal
              buttonLabel="Create Integration"
              modalTitle="Create Integration"
              modalId="integrations/create"
              modalProps={{ size: 'tiny' }}
              buttonProps={{ primary: true }}
              form={<IntegrationsForm />}
            />
            <Button
              primary
              loading={isRefreshingOutcomes}
              onClick={refreshUniqueOutcomesMaterializedView}
              // Explain the button in both states via tooltips. The disabledTooltip
              // matters because a refresh can already be running on load (nightly
              // job or another admin), so a user may see the spinner without having
              // clicked it.
              tooltip="Rebuilds the list of unique call outcomes used for reporting filters. Runs in the background and can take a few minutes."
              disabledTooltip="An outcomes refresh is currently running. It can take a few minutes and continues in the background — you can safely leave this page."
            >
              {isRefreshingOutcomes ? 'Refreshing Outcomes...' : 'Refresh Unique Outcomes'}
            </Button>
          </>
        }
      />

      <Card className="no-padding">
        <AdvancedTable
          defaultOrderBy="name"
          loading={loading}
          columns={columns}
          rows={rows}
          actions={actions}
          stickyAction
          pagination
        />
      </Card>
    </>
  )
}

export default IntegrationsPage
